/**
 * HomePage — /
 *
 * Sections:
 *  1. Hero — full-bleed gradient + wave divider
 *  2. Live Now strip — horizontal scroll of live match mini-cards
 *  3. Sport pills + Featured matches grid with odds buttons
 *  4. AI Picks of the Day
 *  5. How to Bet — 3-step explainer
 *  6. Trust band — 6 items
 *  7. Share section — WhatsApp CTA
 *  8. Footer — brand + legal (inline)
 *
 * Design notes:
 * - Mirrors prizes.my-investments.io design token system (violet/pink/orange gradient, DM Sans)
 * - Shimmer keyframes injected once into <head> via a module-level effect (not re-injected on remount)
 * - getEvents() is called once and results are passed down to Live strip, Featured grid, and AI Picks
 *   to avoid triple-fetching the same endpoint on page load.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import EventCard from '../components/EventCard';
import {
  getSports,
  getEvents,
  getEventDetail,
  getPrediction,
  confidenceBand,
  type Sport,
  type EventSummary,
  type EventDetail,
  type Market,
  type Prediction,
} from '../services/api';
import { useBetSlip } from '../contexts/BetSlipContext';
import { useAuth } from '../contexts/AuthContext';
import { VERSION } from '../version';

// ── Constants ─────────────────────────────────────────────────────────────────

const COMPANY_REG_NO = '2021/853258/07';
const COMPANY_ADDRESS = 'Sandton, Johannesburg, South Africa, 2191';
const SUPPORT_EMAIL = 'info@marome-investments.co.za';
const SUPPORT_WHATSAPP_URL = 'https://wa.me/27791451911';
const GAMBLING_HELPLINE = '0800 006 008';
const SITE_URL = 'https://sports-betting.my-investments.io';
const PRIZES_URL = 'https://prizes.my-investments.io';
const LOTTO_URL = 'https://lotto.my-investments.io';

const WHATSAPP_SHARE_URL = `https://wa.me/?text=${encodeURIComponent(
  'My Bets — AI-assisted sports betting for SA. Live odds + AI insights 👉 ' + SITE_URL,
)}`;

/** Shimmer gradient applied inline so it works without a global CSS class. */
const SHIMMER_STYLE: React.CSSProperties = {
  background: 'linear-gradient(90deg, #f9fafb 25%, #ede9fe 50%, #f9fafb 75%)',
  backgroundSize: '400px 100%',
  animation: 'shimmer 1.4s ease infinite',
};

/**
 * Inject shimmer + scrollbar-hide CSS once into <head> at module load time.
 * This avoids accumulating duplicate <style> nodes when the component remounts
 * (e.g. fast back-navigation), while still keeping the styles co-located with
 * the component that needs them.
 */
const STYLE_ID = 'homepage-shimmer-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes shimmer {
      0%   { background-position: -400px 0; }
      100% { background-position:  400px 0; }
    }
    .scrollbar-hide {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .scrollbar-hide::-webkit-scrollbar { display: none; }
  `;
  document.head.appendChild(el);
}

// ── Utility helpers ───────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-ZA', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Find the match-winner / 1X2 market from an EventDetail's markets array. */
function findMatchWinnerMarket(markets: Market[]): Market | null {
  return (
    markets.find(
      (m) =>
        m.status === 'open' &&
        (m.name.toLowerCase().includes('match winner') ||
          m.name.toLowerCase().includes('1x2') ||
          m.name.toLowerCase() === 'match result'),
    ) ?? null
  );
}

// ── Shimmer building block ────────────────────────────────────────────────────

function ShimmerBlock({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={`rounded ${className ?? ''}`}
      style={SHIMMER_STYLE}
    />
  );
}

// ── Section 2: Live Now strip ─────────────────────────────────────────────────

interface LiveNowStripProps {
  allEvents: EventSummary[];
  loading: boolean;
}

function LiveNowStrip({ allEvents, loading }: LiveNowStripProps) {
  const liveEvents = allEvents.filter((e) => e.status === 'live');

  // Collapse entirely when there are no live events and loading is done
  if (!loading && liveEvents.length === 0) return null;

  return (
    <section
      className="border-b border-border bg-surface-2"
      aria-label="Live matches"
    >
      <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          {/* LIVE badge */}
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-pill bg-cta px-3 py-1 text-xs font-bold text-white">
            <span
              className="inline-block h-2 w-2 animate-pulse rounded-full bg-white"
              aria-hidden="true"
            />
            LIVE
          </span>

          {/* Horizontal scroll */}
          <div
            className="flex flex-1 gap-3 overflow-x-auto pb-1 scrollbar-hide"
            role="list"
            aria-label="Live match cards"
          >
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    aria-hidden="true"
                    className="shrink-0 w-44 rounded-card"
                    style={{ ...SHIMMER_STYLE, height: 56 }}
                  />
                ))
              : liveEvents.map((event) => (
                  <Link
                    key={event.id}
                    to={`/event/${event.id}`}
                    role="listitem"
                    className="shrink-0 flex flex-col justify-center rounded-card border border-cta/30 bg-white px-3 py-2 shadow-card transition hover:border-cta/60 hover:shadow-card-hover min-w-[11rem]"
                  >
                    <p className="truncate text-xs font-semibold text-text-primary">
                      {event.home_team}
                      {event.home_score !== null && (
                        <span className="ml-1 font-bold text-cta">
                          {event.home_score}
                        </span>
                      )}{' '}
                      <span className="text-text-muted">vs</span>{' '}
                      {event.away_team}
                      {event.away_score !== null && (
                        <span className="ml-1 font-bold text-cta">
                          {event.away_score}
                        </span>
                      )}
                    </p>
                    {event.match_time && (
                      <p className="mt-0.5 text-xs font-medium text-cta">
                        {event.match_time}&prime;
                      </p>
                    )}
                  </Link>
                ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Section 3: Match card shimmer + card ──────────────────────────────────────

function MatchCardShimmer() {
  return (
    <li
      aria-hidden="true"
      className="rounded-card border border-border bg-white p-4 shadow-card space-y-3"
    >
      <ShimmerBlock className="h-3 w-1/2" />
      <ShimmerBlock className="h-4 w-3/4" />
      <ShimmerBlock className="h-3 w-1/3" />
      <div className="flex gap-2 mt-2">
        <ShimmerBlock className="h-11 flex-1" />
        <ShimmerBlock className="h-11 flex-1" />
        <ShimmerBlock className="h-11 flex-1" />
      </div>
    </li>
  );
}

interface MatchCardProps {
  event: EventSummary;
  /**
   * null  = detail is currently loading (show shimmer odds buttons)
   * value = detail loaded (show real odds or "View markets →")
   */
  detail: EventDetail | null;
}

function MatchCard({ event, detail }: MatchCardProps) {
  return (
    <li>
      <EventCard event={event} detail={detail} />
    </li>
  );
}

// ── Section 3: Featured matches + sport pills ─────────────────────────────────

interface FeaturedMatchesSectionProps {
  /** All events from the top-level fetch (used to derive filtered list). */
  allEvents: EventSummary[];
  eventsLoading: boolean;
  sports: Sport[];
}

function FeaturedMatchesSection({
  allEvents,
  eventsLoading,
  sports,
}: FeaturedMatchesSectionProps) {
  const [activeSport, setActiveSport] = useState<string | null>(null);
  const [filteredEvents, setFilteredEvents] = useState<EventSummary[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [details, setDetails] = useState<Map<string, EventDetail>>(new Map());
  // Set of IDs whose detail fetch has completed (success or failure)
  const [detailsDone, setDetailsDone] = useState<Set<string>>(new Set());
  const cancelRef = useRef<{ aborted: boolean }>({ aborted: false });

  // Soccer is our premium sport — default the featured view to Football once it
  // appears in the sports list (only auto-set once; the user can still pick "All").
  const didDefaultSport = useRef(false);
  useEffect(() => {
    if (didDefaultSport.current) return;
    if (sports.some((s) => s.slug === 'football')) {
      setActiveSport('football');
      didDefaultSport.current = true;
    }
  }, [sports]);

  // When the top-level events list or the active sport changes, recompute the
  // filtered list. If a sport is selected and is NOT in allEvents (e.g. on
  // initial load where allEvents is empty), re-fetch from the API.
  const applyFilter = useCallback(
    (sportSlug: string | null, events: EventSummary[]) => {
      // Cancel any in-flight sport-specific re-fetch
      cancelRef.current.aborted = true;
      const cancel = { aborted: false };
      cancelRef.current = cancel;

      if (sportSlug === null) {
        // All sports: use the already-fetched top-level list
        const upcoming = events
          .filter((e) => e.status === 'scheduled' || e.status === 'live')
          .slice(0, 6);
        setFilteredEvents(upcoming);
        setFilterLoading(false);
        setDetails(new Map());
        setDetailsDone(new Set());
        fetchDetails(upcoming, cancel);
      } else {
        // Specific sport: check if we can filter client-side first
        const fromCache = events.filter(
          (e) =>
            e.sport_slug === sportSlug &&
            (e.status === 'scheduled' || e.status === 'live'),
        );
        if (fromCache.length > 0) {
          const sliced = fromCache.slice(0, 6);
          setFilteredEvents(sliced);
          setFilterLoading(false);
          setDetails(new Map());
          setDetailsDone(new Set());
          fetchDetails(sliced, cancel);
        } else {
          // Fall back to API filter
          setFilterLoading(true);
          setDetails(new Map());
          setDetailsDone(new Set());
          getEvents(sportSlug)
            .then((res) => {
              if (cancel.aborted) return;
              const sliced = res.data
                .filter((e) => e.status === 'scheduled' || e.status === 'live')
                .slice(0, 6);
              setFilteredEvents(sliced);
              setFilterLoading(false);
              fetchDetails(sliced, cancel);
            })
            .catch(() => {
              if (!cancel.aborted) setFilterLoading(false);
            });
        }
      }

      return () => {
        cancel.aborted = true;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  function fetchDetails(
    events: EventSummary[],
    cancel: { aborted: boolean },
  ) {
    if (events.length === 0) return;
    Promise.allSettled(
      events.map((ev) =>
        getEventDetail(ev.id).then((r) => ({ id: ev.id, data: r.data })),
      ),
    ).then((results) => {
      if (cancel.aborted) return;
      setDetails((prev) => {
        const next = new Map(prev);
        results.forEach((r) => {
          if (r.status === 'fulfilled') next.set(r.value.id, r.value.data);
        });
        return next;
      });
      setDetailsDone(new Set(events.map((e) => e.id)));
    });
  }

  // Re-apply filter whenever sport selection or source events change
  useEffect(() => {
    if (!eventsLoading) {
      const cleanup = applyFilter(activeSport, allEvents);
      return cleanup;
    }
  }, [activeSport, allEvents, eventsLoading, applyFilter]);

  const activeSportName =
    sports.find((s) => s.slug === activeSport)?.name ?? null;
  const isLoading = eventsLoading || filterLoading;
  // Only show "View all" for a specific sport (links to /sport/:slug).
  // When activeSport is null, ALL events are already shown — there's no
  // deeper "all" page to navigate to.
  const showViewAll = !isLoading && activeSport !== null;

  return (
    <section
      id="matches"
      className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 scroll-mt-20"
      aria-label="Featured matches"
    >
      {/* Sport filter pills */}
      <div
        className="mb-6 flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
        role="list"
        aria-label="Sport filters"
      >
        <button
          type="button"
          role="listitem"
          onClick={() => setActiveSport(null)}
          className={`shrink-0 min-h-[36px] rounded-pill px-4 py-1.5 text-sm font-semibold transition ${
            activeSport === null
              ? 'bg-primary text-white shadow-btn'
              : 'border border-border bg-white text-text-secondary hover:border-primary/40'
          }`}
        >
          All
        </button>
        {sports.map((sport) => (
          <button
            key={sport.slug}
            type="button"
            role="listitem"
            onClick={() => setActiveSport(sport.slug)}
            className={`shrink-0 min-h-[36px] rounded-pill px-4 py-1.5 text-sm font-semibold transition ${
              activeSport === sport.slug
                ? 'bg-primary text-white shadow-btn'
                : 'border border-border bg-white text-text-secondary hover:border-primary/40'
            }`}
          >
            {sport.icon ? `${sport.icon} ` : ''}{sport.name}
          </button>
        ))}
      </div>

      {/* Section heading */}
      <h2 className="mb-6 font-heading text-2xl font-bold text-text-primary sm:text-3xl">
        {activeSportName ? `${activeSportName} Matches` : 'Featured Matches'}
      </h2>

      {/* Accessible live region */}
      <p role="status" aria-live="polite" className="sr-only">
        {isLoading ? 'Loading matches…' : ''}
      </p>

      {/* Grid */}
      <ul
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        role="list"
      >
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <MatchCardShimmer key={i} />)
        ) : filteredEvents.length === 0 ? (
          <li className="col-span-full rounded-card border border-border bg-surface-2 p-10 text-center text-sm text-text-muted">
            No matches available right now. Check back soon.
          </li>
        ) : (
          filteredEvents.map((ev) => (
            <MatchCard
              key={ev.id}
              event={ev}
              detail={detailsDone.has(ev.id) ? (details.get(ev.id) ?? null) : null}
            />
          ))
        )}
      </ul>

      {/* View all — only shown when a specific sport is active */}
      {showViewAll && (
        <div className="mt-8 text-center">
          <Link
            to={`/sport/${activeSport}`}
            className="inline-flex min-h-[44px] items-center rounded-button border border-primary px-6 py-2.5 text-sm font-semibold text-primary transition hover:bg-primary/5"
          >
            View all {activeSportName} matches →
          </Link>
        </div>
      )}
    </section>
  );
}

// ── Section 4: AI Picks ───────────────────────────────────────────────────────

interface EventWithPrediction {
  event: EventSummary;
  prediction: Prediction;
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const map = {
    high:   { icon: '🔥', label: 'High confidence',   cls: 'bg-primary/10 text-primary' },
    medium: { icon: '📊', label: 'Medium confidence', cls: 'bg-cta/10 text-cta' },
    low:    { icon: '📉', label: 'Low confidence',    cls: 'bg-surface-2 text-text-muted' },
  } as const;
  const { icon, label, cls } = map[confidenceBand(confidence)];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-pill px-2.5 py-0.5 text-xs font-semibold ${cls}`}
    >
      <span aria-hidden="true">{icon}</span>
      {label}
    </span>
  );
}

function ProbBar({
  label,
  pct,
  color,
}: {
  label: string;
  pct: number;
  color: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-12 shrink-0 text-xs text-text-secondary">{label}</span>
      <div
        className="flex-1 overflow-hidden rounded-bar bg-surface-track h-1.5"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} ${pct}%`}
      >
        <div
          className="h-full rounded-bar transition-all"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
      <span className="w-8 shrink-0 text-right text-xs font-mono font-semibold text-text-primary">
        {pct}%
      </span>
    </div>
  );
}

function AiPickCardShimmer() {
  return (
    <div
      aria-hidden="true"
      className="rounded-card border border-border bg-white p-5 shadow-card space-y-3"
    >
      <ShimmerBlock className="h-4 w-3/4" />
      <ShimmerBlock className="h-3 w-1/2" />
      <ShimmerBlock className="h-5 w-24" />
      <ShimmerBlock className="h-2 w-full" />
      <ShimmerBlock className="h-2 w-4/5" />
      <ShimmerBlock className="h-2 w-3/5" />
      <ShimmerBlock className="h-3 w-1/3" />
    </div>
  );
}

/**
 * AI pick card — the entire card is a link to the event page, mirroring the
 * original code where the card was wrapped in a <Link>. Using a card-level link
 * means users can click anywhere on the card to navigate, not just the text.
 */
function AiPickCard({ event, prediction }: EventWithPrediction) {
  const safePct = (p: number) => {
    const v = Math.round((p ?? 0) * 100);
    return Number.isFinite(v) ? Math.max(0, Math.min(100, v)) : 0;
  };
  const homePct = safePct(prediction.home_prob);
  const drawPct = safePct(prediction.draw_prob);
  const awayPct = safePct(prediction.away_prob);

  return (
    <Link
      to={`/event/${event.id}`}
      className="flex flex-col gap-3 rounded-card border border-primary/15 bg-white p-5 shadow-card transition hover:border-primary/30 hover:shadow-card-hover"
    >
      <div>
        <p className="font-heading text-sm font-bold text-text-primary truncate">
          {event.home_team} vs {event.away_team}
        </p>
        <p className="mt-0.5 text-xs text-text-muted">{event.league}</p>
      </div>

      <ConfidenceBadge confidence={prediction.confidence} />

      <div className="space-y-1.5">
        <ProbBar label="Home" pct={homePct} color="#7c3aed" />
        <ProbBar label="Draw" pct={drawPct} color="#9ca3af" />
        <ProbBar label="Away" pct={awayPct} color="#ec4899" />
      </div>

      {prediction.explanation && (
        <p className="line-clamp-2 text-xs leading-relaxed text-text-secondary">
          {prediction.explanation}
        </p>
      )}

      <span className="mt-auto text-xs font-semibold text-primary">
        View event →
      </span>
    </Link>
  );
}

interface AiPicksSectionProps {
  allEvents: EventSummary[];
  eventsLoading: boolean;
}

function AiPicksSection({ allEvents, eventsLoading }: AiPicksSectionProps) {
  const [picks, setPicks] = useState<EventWithPrediction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (eventsLoading) return; // wait for events to be fetched
    let active = true;
    const run = async () => {
      try {
        const candidates = allEvents
          .filter((e) => e.status === 'scheduled' || e.status === 'live')
          .slice(0, 10);

        const settled = await Promise.allSettled(
          candidates.map(async (ev) => {
            const pred = await getPrediction(ev.id);
            return { event: ev, prediction: pred.data };
          }),
        );

        const successful = settled
          .filter(
            (r): r is PromiseFulfilledResult<EventWithPrediction> =>
              r.status === 'fulfilled',
          )
          .map((r) => r.value)
          .slice(0, 3);

        if (active) setPicks(successful);
      } catch {
        // Section collapses silently on error — no broken UI
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [allEvents, eventsLoading]);

  if (!loading && picks.length === 0) return null;

  return (
    <section
      className="border-y border-border bg-violet-50/40 py-14"
      aria-label="AI match predictions"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-center">
          <p className="text-3xl" aria-hidden="true">
            {'🤖'}
          </p>
          <h2 className="mt-2 font-heading text-2xl font-bold text-text-primary sm:text-3xl">
            AI Match Predictions
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-text-secondary sm:text-base">
            Our AI analyses form, head-to-head records, and market movement to
            surface the picks with highest conviction.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <AiPickCardShimmer key={i} />
              ))
            : picks.map(({ event, prediction }) => (
                <AiPickCard
                  key={event.id}
                  event={event}
                  prediction={prediction}
                />
              ))}
        </div>

        <p className="mt-8 text-center text-xs text-text-muted">
          <span aria-hidden="true">{'⚠️'}</span> AI predictions are
          for entertainment only. They do not constitute betting advice.
        </p>
      </div>
    </section>
  );
}

// ── Section 5: How to Bet ─────────────────────────────────────────────────────

const HOW_TO_BET_STEPS = [
  {
    title: 'Create an account',
    body: 'Sign up in under a minute — just your email and a password. No long forms.',
  },
  {
    title: 'Deposit funds',
    body: 'Top up securely via PayStack using card or instant EFT. Funds reflect immediately.',
  },
  {
    title: 'Pick your match and bet',
    body: 'Browse live and upcoming fixtures, choose your market, add to your bet slip, and confirm.',
  },
] as const;

function HowToBetSection() {
  return (
    <section
      id="how-to-bet"
      className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 scroll-mt-20"
      aria-label="How to bet"
    >
      <div className="mb-10 text-center">
        <h2 className="font-heading text-2xl font-bold text-text-primary sm:text-3xl">
          How to Bet — 3 Easy Steps
        </h2>
        <p className="mt-2 text-sm text-text-secondary">
          Place your first bet in under 5 minutes.
        </p>
      </div>

      <ol className="grid grid-cols-1 gap-6 sm:grid-cols-3" role="list">
        {HOW_TO_BET_STEPS.map((step, idx) => (
          <li
            key={step.title}
            className="rounded-card border border-border bg-white p-6 shadow-card"
          >
            <span
              className="flex h-11 w-11 items-center justify-center rounded-full text-lg font-bold text-white"
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)',
              }}
              aria-hidden="true"
            >
              {idx + 1}
            </span>
            <h3 className="mt-4 font-heading text-base font-bold text-text-primary">
              {step.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-text-secondary">
              {step.body}
            </p>
          </li>
        ))}
      </ol>

      <div className="mt-10 text-center">
        <Link
          to="/register"
          className="inline-flex min-h-[48px] items-center justify-center rounded-button px-8 py-3 text-base font-bold text-white shadow-btn transition hover:opacity-90 focus:outline-none focus:ring-4 focus:ring-primary/30"
          style={{
            background:
              'linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #f97316 100%)',
          }}
        >
          Get started — Sign up free
        </Link>
      </div>
    </section>
  );
}

// ── Section 6: Trust band ─────────────────────────────────────────────────────

const TRUST_ITEMS = [
  {
    icon: '🌍',
    title: 'Offshore licensed operator',
    body: 'Operating under an offshore gaming licence with full regulatory oversight.',
  },
  {
    icon: '🔒',
    title: 'Secure payments via PayStack',
    body: 'All deposits and withdrawals processed by PayStack — PCI-DSS compliant.',
  },
  {
    icon: '🤖',
    title: 'AI-powered insights',
    body: 'AI insights from match form, H2H data, and market movement — for information only, never a guarantee of outcomes.',
  },
  {
    icon: '⚡',
    title: 'Live in-play betting',
    body: 'Bet on matches as they happen with odds that update in real time.',
  },
  {
    icon: '🏢',
    title: 'Marome Investments',
    body: `CIPC ${COMPANY_REG_NO}. Registered in Sandton, Johannesburg.`,
  },
  {
    icon: '🎰',
    title: 'Responsible gambling tools',
    body: 'Self-exclusion, deposit limits, and a free 24/7 helpline: 0800 006 008.',
  },
] as const;

function TrustBand() {
  return (
    <section
      id="trust"
      className="border-y border-border bg-violet-50/40 py-16 scroll-mt-20"
      aria-label="Why you can trust us"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="mb-10 text-center font-heading text-2xl font-bold text-text-primary sm:text-3xl">
          Why you can trust us
        </h2>
        <ul
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          role="list"
        >
          {TRUST_ITEMS.map((item) => (
            <li
              key={item.title}
              className="flex gap-3 rounded-card border border-border bg-white p-5 shadow-card"
            >
              <span className="text-2xl" aria-hidden="true">
                {item.icon}
              </span>
              <div>
                <h3 className="text-sm font-bold text-text-primary">
                  {item.title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                  {item.body}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ── Section 7: Share ──────────────────────────────────────────────────────────

function ShareSection() {
  return (
    <section className="bg-white py-12 text-center" aria-label="Share">
      <div className="mx-auto max-w-2xl px-4">
        <h2 className="mb-2 font-heading text-2xl font-bold text-primary">
          Tell a friend about My Bets
        </h2>
        <p className="mb-6 text-sm text-text-secondary sm:text-base">
          AI-assisted sports betting for South Africa — live odds with AI-powered
          insights. For entertainment — bet responsibly.
        </p>
        <a
          href={WHATSAPP_SHARE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share My Bets on WhatsApp"
          className="inline-flex min-h-[44px] items-center gap-2 rounded-button bg-[#25D366] px-6 py-3 font-semibold text-white transition hover:bg-[#128C7E]"
        >
          {/* WhatsApp SVG icon */}
          <svg
            className="h-5 w-5"
            fill="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          Share on WhatsApp
        </a>
      </div>
    </section>
  );
}

// ── Section 8: Inline footer ──────────────────────────────────────────────────

function HomeFooter() {
  return (
    <footer className="border-t border-border bg-white py-10" aria-label="Site footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Top row */}
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          {/* Brand */}
          <div>
            <p className="font-heading text-lg font-bold text-primary">
              {'🎯'} My Bets
            </p>
            <p className="mt-1 text-sm text-text-muted">
              AI-assisted sports betting for South Africa
            </p>
          </div>

          {/* Navigation */}
          <nav
            className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-text-secondary"
            aria-label="Footer navigation"
          >
            <a
              href="#matches"
              className="hover:text-primary transition-colors"
            >
              Matches
            </a>
            <a
              href="#how-to-bet"
              className="hover:text-primary transition-colors"
            >
              How to Bet
            </a>
            <Link to="/about" className="hover:text-primary transition-colors">
              About
            </Link>
            <Link
              to="/responsible-gambling"
              className="hover:text-primary transition-colors"
            >
              Responsible Gambling
            </Link>
            <Link to="/terms" className="hover:text-primary transition-colors">
              Terms &amp; Betting Rules
            </Link>
            <Link to="/privacy" className="hover:text-primary transition-colors">
              Privacy
            </Link>
            <Link to="/complaints" className="hover:text-primary transition-colors">
              Complaints
            </Link>
            <Link to="/login" className="hover:text-primary transition-colors">
              Log in
            </Link>
            <Link
              to="/register"
              className="hover:text-primary transition-colors"
            >
              Sign up
            </Link>
            {/* Sister products */}
            <a
              href={PRIZES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary transition-colors hover:text-primary-dark"
            >
              Win a Prize {'↗'}
            </a>
            <a
              href={LOTTO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary transition-colors hover:text-primary-dark"
            >
              Play the Lotto {'↗'}
            </a>
          </nav>
        </div>

        {/* Responsible gambling notice */}
        <div className="mt-6 rounded-card border border-warning/30 bg-warning/5 px-4 py-3">
          <p className="text-sm text-text-secondary">
            {'🎰'}{' '}
            <strong className="text-text-primary">
              Responsible Gambling Helpline:
            </strong>{' '}
            <a
              href={`tel:${GAMBLING_HELPLINE.replace(/\s/g, '')}`}
              className="font-semibold text-warning hover:underline"
            >
              {GAMBLING_HELPLINE}
            </a>{' '}
            &mdash; free, confidential, 24/7. Betting is for adults 18+.
          </p>
        </div>

        {/* Legal line */}
        <div className="mt-6 border-t border-border pt-6 text-center text-xs leading-relaxed text-text-muted">
          <p>
            &copy; {new Date().getFullYear()} My Bets / Marome Investments (Pty)
            Ltd &middot; CIPC {COMPANY_REG_NO} &middot; {COMPANY_ADDRESS}
          </p>
          <p className="mt-1">
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="hover:text-primary transition-colors"
            >
              {SUPPORT_EMAIL}
            </a>
            {' · '}
            <a
              href={SUPPORT_WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              WhatsApp us
            </a>
            {' · '}
            <span>v{VERSION}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

// ── Page root ─────────────────────────────────────────────────────────────────

export default function HomePage() {
  const { user } = useAuth();

  // ── Single top-level events fetch ───────────────────────────────────────────
  // Fetched once and passed down to LiveNowStrip, FeaturedMatchesSection, and
  // AiPicksSection to avoid triple-fetching the same endpoint on page load.
  const [allEvents, setAllEvents] = useState<EventSummary[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [sports, setSports] = useState<Sport[]>([]);

  useEffect(() => {
    let active = true;
    getEvents()
      .then((res) => {
        if (active) {
          setAllEvents(res.data);
          setEventsLoading(false);
        }
      })
      .catch(() => {
        if (active) setEventsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    getSports()
      .then((res) => {
        if (active) setSports(res.data);
      })
      .catch(() => {/* silently ignore */});
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-white font-sans">
      <Header />

      <main className="flex-1">
        {/* ── 1. Hero ─────────────────────────────────────────────────────── */}
        <section
          className="relative overflow-hidden"
          aria-label="Hero"
          style={{
            background:
              'linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #f97316 100%)',
          }}
        >
          <div className="mx-auto max-w-6xl px-4 py-20 text-center sm:px-6 sm:py-28 lg:px-8 lg:py-36">
            {/* Feature chip */}
            <p className="mx-auto inline-flex items-center rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/30 sm:text-base">
              {'🤖'} AI Predictions &nbsp;&middot;&nbsp;{' '}
              {'⚡'} Live In-Play &nbsp;&middot;&nbsp;{' '}
              {'🔐'} Licensed Operator
            </p>

            {/* Headline */}
            <h1 className="mt-6 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              {user
                ? `Welcome back, ${user.display_name ?? user.email.split('@')[0]}`
                : 'My Bets — AI-assisted sports betting for South Africa'}
            </h1>

            {/* Subheadline */}
            <p className="mx-auto mt-6 max-w-2xl text-lg text-white/90 sm:text-xl">
              Live odds with AI-powered insights. For entertainment — bet responsibly.
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <a
                href="#matches"
                className="inline-flex min-h-[48px] items-center justify-center rounded-button bg-white px-8 py-3 text-base font-bold text-primary shadow-lg transition hover:bg-violet-50 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-white/50"
              >
                Browse Matches
              </a>
              {!user && (
                <Link
                  to="/register"
                  className="inline-flex min-h-[48px] items-center justify-center rounded-button border-2 border-white/80 bg-transparent px-8 py-3 text-base font-bold text-white transition hover:bg-white/10 focus:outline-none focus:ring-4 focus:ring-white/50"
                >
                  Sign Up Free
                </Link>
              )}
            </div>
          </div>

          {/* Wave divider — exact path from prizes LandingPage.tsx */}
          <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
            <svg
              viewBox="0 0 1440 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="w-full"
              preserveAspectRatio="none"
            >
              <path
                d="M0 80L1440 80L1440 20C1200 80 960 0 720 40C480 80 240 0 0 20L0 80Z"
                fill="white"
              />
            </svg>
          </div>
        </section>

        {/* ── World Cup banner — soccer is the premium sport ───────────────── */}
        {sports.some((s) => s.slug === 'football') && (
          <Link
            to="/sport/football"
            className="block bg-gradient-to-r from-violet-600 to-pink-500 text-white"
          >
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
              <p className="font-heading text-sm font-bold sm:text-base">
                ⚽ FIFA World Cup 2026 is here — bet every match
              </p>
              <span className="shrink-0 rounded-pill bg-white/20 px-3 py-1 text-xs font-bold ring-1 ring-white/30">
                Bet now →
              </span>
            </div>
          </Link>
        )}

        {/* ── 2. Live Now strip ────────────────────────────────────────────── */}
        <LiveNowStrip allEvents={allEvents} loading={eventsLoading} />

        {/* ── 3. Sport pills + Featured matches ───────────────────────────── */}
        <FeaturedMatchesSection
          allEvents={allEvents}
          eventsLoading={eventsLoading}
          sports={sports}
        />

        {/* ── 4. AI Picks of the Day ──────────────────────────────────────── */}
        <AiPicksSection allEvents={allEvents} eventsLoading={eventsLoading} />

        {/* ── 5. How to Bet ────────────────────────────────────────────────── */}
        <HowToBetSection />

        {/* ── 6. Trust band ────────────────────────────────────────────────── */}
        <TrustBand />

        {/* ── 7. Share ─────────────────────────────────────────────────────── */}
        <ShareSection />
      </main>

      {/* ── 8. Inline footer ─────────────────────────────────────────────── */}
      <HomeFooter />
    </div>
  );
}
